import { getLeaderboardUserDb } from '../firebaseConfig'
import { fetchLeaderboard } from '../apis/gameApi'

export interface LeaderboardEntry {
  id: string
  userId: string
  fullName: string
  email: string
  company: string
  score: number
  totalTime?: number // Total time in seconds to complete all questions
  rank: number
  previousRank?: number
  rankChange: 'up' | 'down' | 'same'
  lastUpdated: number
  initials: string
  color: string
}

// Generate user initials from full name
export const generateInitials = (fullName: string): string => {
  if (!fullName) return 'UN'
  const names = fullName.trim().split(' ')
  if (names.length === 1) {
    return names[0].substring(0, 2).toUpperCase()
  }
  return (names[0][0] + names[names.length - 1][0]).toUpperCase()
}

// Generate deterministic avatar color from userId
export const generateAvatarColor = (userId: string): string => {
  const colors = ['#ffacad', '#ffeb65', '#96c9c8', '#ffbe6f', '#e99bff']
  const index = parseInt(userId.slice(-1), 16) % colors.length
  return colors[index]
}

const POLL_INTERVAL_MS = 5_000

/**
 * Subscribe to leaderboard updates via polling.
 * Calls `callback` immediately with the first result, then every 5 seconds.
 * Returns an unsubscribe function that stops polling when called.
 */
export const subscribeToLeaderboard = (callback: (leaderboard: LeaderboardEntry[]) => void) => {
  // Track previous ranks between poll cycles so we can compute rankChange
  const previousRankMap = new Map<string, number>()

  const poll = async () => {
    try {
      const { leaderboard } = await fetchLeaderboard()

      const entries: LeaderboardEntry[] = leaderboard.map((item) => {
        const userId = item.user_id.toString()
        const previousRank = previousRankMap.get(userId)

        let rankChange: LeaderboardEntry['rankChange'] = 'same'
        if (previousRank !== undefined) {
          if (item.rank < previousRank) rankChange = 'up'
          else if (item.rank > previousRank) rankChange = 'down'
        }

        // Update map for next cycle
        previousRankMap.set(userId, item.rank)

        return {
          id: userId,
          userId,
          fullName: item.full_name || 'Unknown User',
          email: '',
          company: item.company || '',
          score: item.score || 0,
          totalTime: item.total_time || 0,
          rank: item.rank,
          previousRank,
          rankChange,
          lastUpdated: Date.now(),
          initials: generateInitials(item.full_name || 'Unknown User'),
          color: generateAvatarColor(userId),
        }
      })

      callback(entries)
    } catch (error) {
      console.error('Error polling leaderboard:', error)
      // On first poll failure, call callback with empty array so loading state resolves
      if (previousRankMap.size === 0) {
        callback([])
      }
    }
  }

  // Fire immediately, then on every interval
  poll()
  const intervalId = window.setInterval(poll, POLL_INTERVAL_MS)

  return () => window.clearInterval(intervalId)
}

// Update user score and rank (called from backend, but useful for testing)
export const updateUserScore = async (
  userId: string,
  userData: {
    fullName: string
    email: string
    company: string
    score: number
  }
) => {
  const userRef = getLeaderboardUserDb(userId)

  try {
    // Get current user data to calculate rank change
    const currentSnapshot = await userRef.once('value')
    const currentData = currentSnapshot.val()
    const currentRank = currentData?.rank || null

    // Generate initials and color if not exists
    const initials = currentData?.initials || generateInitials(userData.fullName)
    const color = currentData?.color || generateAvatarColor(userId)

    // Update user data
    await userRef.update({
      userId,
      fullName: userData.fullName,
      email: userData.email,
      company: userData.company,
      score: userData.score,
      previousRank: currentRank,
      lastUpdated: Date.now(),
      initials,
      color,
    })

    // Note: Rank calculation and rankChange will be handled by a Firebase Cloud Function
    // or by the backend when it updates the leaderboard

    return true
  } catch (error) {
    console.error('Error updating user score:', error)
    return false
  }
}

// Get user's current leaderboard position
export const getUserLeaderboardPosition = async (userId: string): Promise<LeaderboardEntry | null> => {
  try {
    const userSnapshot = await getLeaderboardUserDb(userId).once('value')
    if (!userSnapshot.exists()) {
      return null
    }

    const userData = userSnapshot.val()
    return {
      id: userId,
      userId,
      fullName: userData.fullName || 'Unknown User',
      email: userData.email || '',
      company: userData.company || '',
      score: userData.score || 0,
      totalTime: userData.totalTime || 0,
      rank: userData.rank || 0,
      previousRank: userData.previousRank,
      rankChange: userData.rankChange || 'new',
      lastUpdated: userData.lastUpdated || Date.now(),
      initials: userData.initials || generateInitials(userData.fullName || 'Unknown User'),
      color: userData.color || generateAvatarColor(userId),
    }
  } catch (error) {
    console.error('Error getting user leaderboard position:', error)
    return null
  }
}
